import React, { useEffect, useState } from 'react'

import LoadingSpinner from '../components/LoadingSpinner'

/**
 * Public (unauthenticated) read-only case review page for physician reviewers.
 * Accessed via a magic link: /review/:token
 */
const ReviewPage = ({ match }) => {
  const { token } = match.params
  const [snapshot, setSnapshot] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    if (!token) {
      setError('Invalid review link.')
      setIsLoading(false)
      return
    }

    fetch(`/api/review/${token}`, {
      headers: { Accept: 'application/json' },
    })
      .then(async res => {
        if (res.status === 404) {
          setError('This review link has expired or is invalid. Please request a new link from the case manager.')
          return
        }
        if (!res.ok) {
          setError('Unable to load case information. Please try again later.')
          return
        }
        const data = await res.json()
        setSnapshot(data)
      })
      .catch(() => setError('A network error occurred. Please check your connection and try again.'))
      .finally(() => setIsLoading(false))
  }, [token])

  if (isLoading) return <LoadingSpinner />

  if (error) {
    return (
      <div className="container mt-5">
        <div className="alert alert-warning">
          <strong>Access Unavailable</strong>
          <p className="mb-0 mt-1">{error}</p>
        </div>
      </div>
    )
  }

  return (
    <div className="container mt-4 mb-5" style={{ maxWidth: 760 }}>
      <div className="mb-4">
        <img src="/logo.png" alt="GuideSmart" style={{ height: 40 }} onError={e => (e.target.style.display = 'none')} />
      </div>

      <h2 className="mb-1">Independent Medical Examination Review</h2>
      <p className="text-muted mb-4">Case #{snapshot.caseNumber} &nbsp;&bull;&nbsp; Colorado Rule 16</p>

      <section className="mb-4">
        <h5 className="border-bottom pb-2">Claim Information</h5>
        <dl className="row mb-0">
          <dt className="col-sm-4">Claimant</dt>
          <dd className="col-sm-8">{snapshot.claimantName || '—'}</dd>

          <dt className="col-sm-4">Claim Number</dt>
          <dd className="col-sm-8">{snapshot.claimNumber || '—'}</dd>

          <dt className="col-sm-4">Date of Injury</dt>
          <dd className="col-sm-8">
            {snapshot.dateOfInjury ? new Date(snapshot.dateOfInjury).toLocaleDateString('en-US') : '—'}
          </dd>

          <dt className="col-sm-4">Employer</dt>
          <dd className="col-sm-8">{snapshot.employerName || '—'}</dd>
        </dl>
      </section>

      <section className="mb-4">
        <h5 className="border-bottom pb-2">Adjuster Contact</h5>
        <dl className="row mb-0">
          <dt className="col-sm-4">Name</dt>
          <dd className="col-sm-8">{snapshot.adjusterName || '—'}</dd>

          <dt className="col-sm-4">Phone</dt>
          <dd className="col-sm-8">{snapshot.adjusterPhone || '—'}</dd>

          <dt className="col-sm-4">Email</dt>
          <dd className="col-sm-8">
            {snapshot.adjusterEmail ? <a href={`mailto:${snapshot.adjusterEmail}`}>{snapshot.adjusterEmail}</a> : '—'}
          </dd>
        </dl>
      </section>

      <section className="mb-4">
        <h5 className="border-bottom pb-2">Case Manager</h5>
        <p className="mb-0">{snapshot.caseManagerName || '—'}</p>
      </section>

      {snapshot.questions && (
        <section className="mb-4">
          <h5 className="border-bottom pb-2">Questions for the Reviewing Physician</h5>
          <div
            className="reviewer-questions"
            // Questions are stored as case-manager-entered plain text notes
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{ __html: snapshot.questions }}
          />
        </section>
      )}

      <section className="mb-5">
        <h5 className="border-bottom pb-2">Medical Records</h5>
        <p className="text-muted">
          Medical records will be provided to you directly by the case manager via email or secure upload.
        </p>
      </section>

      <hr />
      <p className="text-muted small text-center">
        This link expires on{' '}
        {snapshot.expiresAt
          ? new Date(snapshot.expiresAt).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
          : 'N/A'}
        . For questions, contact the case manager.
      </p>
    </div>
  )
}

export default ReviewPage
